import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import "../App.css";
import logo from "../assets/logo.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";

const EditUser = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    patientId: "",
    doctorId: "",
    password: "",
    confirmPassword: "",
    skipVerification: false,
  });
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch doctors and user data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch doctors
        setLoadingDoctors(true);
        const doctorsSnapshot = await getDocs(collection(db, "doctors"));
        const doctorsList = doctorsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDoctors(doctorsList);
        setLoadingDoctors(false);

        // Fetch user data
        // Note: userId is the email address (document ID in Firestore)
        if (userId) {
          setLoading(true);
          const userRef = doc(db, "users", decodeURIComponent(userId));
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            setError("User not found");
            setLoading(false);
            return;
          }

          const userData = userSnap.data();
          setFormData({
            email: userData.email || "",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            phoneNumber: userData.phoneNumber || "",
            patientId: userData.patientId || "",
            doctorId: userData.doctorId || "",
            password: "", // Don't load password
            confirmPassword: "", // Don't load password
            skipVerification: userData.isVerified || false,
          });
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load user data");
        setLoading(false);
        setLoadingDoctors(false);
      }
    };

    fetchData();
  }, [userId]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validateForm = () => {
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError("Email, First Name, and Last Name are required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Invalid email address");
      return false;
    }

    // Only validate password if it's being changed
    if (formData.password) {
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters");
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return false;
      }
    }

    if (formData.phoneNumber && formData.phoneNumber.trim() !== "") {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        setError("Invalid phone number format");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const userRef = doc(db, "users", decodeURIComponent(userId));
      
      // Combine firstName and lastName for username (backward compatibility)
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();

      // Get doctor info if selected
      let doctorInfo = null;
      if (formData.doctorId) {
        const selectedDoctor = doctors.find((doc) => doc.id === formData.doctorId);
        if (selectedDoctor) {
          doctorInfo = {
            doctorId: selectedDoctor.id,
            doctorName: selectedDoctor.name,
            doctorEmail: selectedDoctor.email,
            doctorSpecialty: selectedDoctor.specialty || null,
          };
        }
      }

      // Get existing user data to preserve fields we don't update
      const userSnap = await getDoc(userRef);
      const existingData = userSnap.data();

      const userData = {
        ...existingData, // Preserve existing data
        username: fullName, // Update username
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber || null,
        patientId: formData.patientId || null,
        doctorId: doctorInfo?.doctorId || null,
        doctorName: doctorInfo?.doctorName || null,
        doctorEmail: doctorInfo?.doctorEmail || null,
        doctorSpecialty: doctorInfo?.doctorSpecialty || null,
        isVerified: formData.skipVerification,
        updatedAt: new Date().toISOString(),
      };

      // Only update password if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      await setDoc(userRef, userData);
      console.log("✅ User document updated");

      setSuccess("User updated successfully!");
      
      // Optionally navigate back after a delay
      setTimeout(() => {
        navigate("/users");
      }, 2000);
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Failed to update user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="appointments-container">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-container">
      {/* Sidebar */}
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/doctors">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      {/* Main Content */}
      <div className="appointments-page">
        <h1 className="dashboard-title">Edit User</h1>

        {error && (
          <div
            className="error-message"
            style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="success-message"
            style={{
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            maxWidth: "600px",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Email Address *</label>
              <input
                type="email"
                className="login-input"
                name="email"
                placeholder="user@example.com"
                required
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">First Name *</label>
              <input
                type="text"
                className="login-input"
                name="firstName"
                placeholder="John"
                required
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Last Name *</label>
              <input
                type="text"
                className="login-input"
                name="lastName"
                placeholder="Doe"
                required
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Phone Number</label>
              <input
                type="tel"
                className="login-input"
                name="phoneNumber"
                placeholder="+1 (555) 123-4567"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Patient ID</label>
              <input
                type="text"
                className="login-input"
                name="patientId"
                placeholder="Enter patient ID (optional)"
                value={formData.patientId}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Assigned Doctor</label>
              <select
                className="login-input"
                name="doctorId"
                value={formData.doctorId}
                onChange={handleInputChange}
                disabled={loading || loadingDoctors}
                style={{
                  padding: "12px",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  width: "100%",
                  backgroundColor: loading || loadingDoctors ? "#f5f5f5" : "#fff",
                }}
              >
                <option value="">Select a doctor (optional)</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name} {doctor.specialty ? `- ${doctor.specialty}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">New Password (leave blank to keep current)</label>
              <input
                type="password"
                className="login-input"
                name="password"
                placeholder="Leave blank to keep current password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={loading}
                minLength={6}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label className="input-label">Confirm New Password</label>
              <input
                type="password"
                className="login-input"
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={loading}
                minLength={6}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  name="skipVerification"
                  checked={formData.skipVerification}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span>User is verified</span>
              </label>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="submit"
                className="login-btn"
                disabled={loading}
                style={{
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  flex: 1,
                }}
              >
                {loading ? "Updating..." : "Update User"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/users")}
                className="login-btn"
                disabled={loading}
                style={{
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                  backgroundColor: "#6c757d",
                  flex: 1,
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;

